import {
  IAccountService,
  IPropertySelection,
  IUserContext,
} from "@origin-digital/event-dispatcher";
import { useEffect, useState } from "react";
import { writePropertyToStorage } from "../UserContext/UserContextService";
import {
  copyAndReplaceProperty,
  getAllPropertiesForUser,
  readPropertyIdsFromStorage,
  selectServiceOnProperty,
} from "../utils/properties";
import { usePropertySelectionListener } from "./usePropertySelectionListener";

export interface PropertySelectionProps {
  properties: IPropertySelection[] | undefined;
  property: IPropertySelection | null;
  setProperty: (property: IPropertySelection | null) => void;
  setService: (accountService: IAccountService) => void;
}

/**
 * Handle state for properties list, selected property and setting selected property and selected service.
 *
 * Initialise state based on user data and local storage.
 *
 * Listen for SUBSCRIBE_SELECTED_PROPERTY subscribers and send updates on selection changes.
 */
export const usePropertySelection = (
  user?: IUserContext
): PropertySelectionProps => {
  const [properties, setProperties] = useState<
    IPropertySelection[] | undefined
  >(undefined);
  const [propertySelection, setPropertySelection] =
    useState<IPropertySelection | null>(null);

  // when user is loaded initialise the properties list and selection
  useEffect(() => {
    if (user) {
      const allProperties = getAllPropertiesForUser(user);
      const storagePropertyIds = readPropertyIdsFromStorage();
      const storagePropertyInList = allProperties.find(
        (prop) => prop.id === storagePropertyIds?.propertyId
      );
      selectServiceOnProperty(
        storagePropertyInList,
        storagePropertyIds?.selectedServiceId
      );
      setProperties(allProperties);
      setPropertySelection(storagePropertyInList ?? null);
    }
  }, [user]);

  // listen for subscription events, and notify subscribers when the selection changes
  const { onPropertySelectionChange } =
    usePropertySelectionListener(propertySelection);
  useEffect(() => {
    onPropertySelectionChange(propertySelection);
  }, [propertySelection]);

  // handler: when a property selection is made, write it to storage
  const setProperty = (value: IPropertySelection | null) => {
    writePropertyToStorage(value);
    setPropertySelection(value);
  };

  // handler: when a service selection is made, update current property selection, write it to storage, and update properties list
  const setService = (selectedAccountService: IAccountService) => {
    if (!propertySelection) {
      return;
    }
    const newPropertySelection: IPropertySelection = {
      ...propertySelection,
      selectedAccountService,
    };
    const newPropertiesList = copyAndReplaceProperty(
      properties,
      newPropertySelection
    );
    setProperties(newPropertiesList);
    writePropertyToStorage(newPropertySelection);
    setPropertySelection(newPropertySelection);
  };

  return { properties, property: propertySelection, setProperty, setService };
};
